import { Button } from "@/components/ui/button";
import { scrollToForm, trackEvent } from "@/lib/tracking";
import { X, Check, ArrowRight, Inbox, MessageCircle, Table2, AlertTriangle, Mail, Repeat, KanbanSquare, BarChart3 } from "lucide-react";

const ACCENT = "#BEF264";

const PROBLEMS = [
  { Icon: Mail, title: "Enquiries are scattered", copy: "Customer details are spread across emails, forms and spreadsheets." },
  { Icon: Repeat, title: "Follow-up is manual", copy: "Someone must remember to respond or follow up." },
  { Icon: KanbanSquare, title: "Sales opportunities are difficult to track", copy: "Your team cannot easily see which enquiries are new, qualified or awaiting action." },
  { Icon: BarChart3, title: "Marketing results are unclear", copy: "It is difficult to connect customer enquiries back to campaigns and sales outcomes." },
];

function ChaosSide() {
  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-5">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-rose-700">
        <AlertTriangle className="h-3.5 w-3.5" /> Without a connected system
      </div>
      <div className="mt-4 space-y-2.5">
        {[
          { Icon: Inbox, label: "Enquiries in an inbox", meta: "unread" },
          { Icon: Table2, label: "Leads in a spreadsheet", meta: "no owner" },
          { Icon: MessageCircle, label: "WhatsApp chats", meta: "unassigned" },
        ].map(({ Icon, label, meta }) => (
          <div key={label} className="flex items-center gap-3 rounded-xl border border-rose-200/70 bg-white px-3.5 py-2.5">
            <div className="h-8 w-8 rounded-lg bg-rose-100 text-rose-500 flex items-center justify-center"><Icon className="h-4 w-4" /></div>
            <span className="text-sm font-semibold text-zinc-800">{label}</span>
            <span className="ml-auto text-[11px] font-medium text-rose-700">{meta}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-rose-500/10 text-rose-700 px-3 py-1 text-[11px] font-bold">
        <X className="h-3 w-3" /> No clear next step
      </div>
    </div>
  );
}

function OrderSide() {
  return (
    <div className="rounded-2xl border p-5" style={{ borderColor: "#0B1120", background: "#0B1120" }}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider" style={{ color: ACCENT }}>
        <Check className="h-3.5 w-3.5" /> With a Rekisa Sales System
      </div>
      <div className="mt-4 space-y-2.5">
        <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.05] px-3.5 py-2.5">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-black text-zinc-900" style={{ background: ACCENT }}>NK</div>
            <div>
              <div className="text-sm font-bold text-white leading-tight">Naledi K. (example)</div>
              <div className="text-[10px] text-white/50">Owner assigned · Source recorded</div>
            </div>
          </div>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${ACCENT}22`, color: ACCENT }}>Contacted</span>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.05] px-3.5 py-2.5 text-[11px] text-white/60">
          Acknowledgement sent · reminder scheduled · pipeline stage set
        </div>
      </div>
      <div className="mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold text-zinc-900" style={{ background: ACCENT }}>
        <ArrowRight className="h-3 w-3" /> Next action: call today
      </div>
    </div>
  );
}

export default function SalesProblem() {
  const handleCta = () => {
    trackEvent("cta_click", { location: "sales_problem", label: "connect_my_follow_up" });
    scrollToForm();
  };

  return (
    <section id="problems" aria-labelledby="problems-heading" data-testid="web-problem-section" className="relative py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-zinc-600 font-semibold mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Website lead management
          </div>
          <h2 id="problems-heading" className="font-display text-4xl sm:text-5xl lg:text-[56px] font-black tracking-[-0.03em] leading-[1.03] text-zinc-900">
            Are Potential Customers Getting Lost After They Enquire?
          </h2>
          <p className="mt-6 text-zinc-600 leading-relaxed text-base sm:text-lg">
            A customer visits your website, completes a form and expects a response. But if that enquiry lands in an inbox,
            spreadsheet or disconnected tool, your team may struggle to track who owns it, what the customer needs and what
            should happen next.
          </p>
          <p className="mt-3 text-zinc-700 font-semibold">Rekisa helps connect your website to a structured sales process.</p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PROBLEMS.map(({ Icon, title, copy }, i) => (
            <div key={title} data-testid={`sales-problem-card-${i}`} className="rounded-2xl border border-zinc-200 bg-white p-5">
              <div className="h-10 w-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center"><Icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-lg font-black text-zinc-900 leading-snug">{title}</h3>
              <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-5 items-stretch">
          <ChaosSide />
          <OrderSide />
        </div>

        <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 rounded-3xl bg-sales-navy p-7 sm:p-10">
          <p className="font-display text-2xl sm:text-3xl font-black tracking-[-0.02em] text-white leading-tight max-w-xl">
            Connect your website, <span style={{ color: ACCENT }}>customer information and follow-up process.</span>
          </p>
          <Button
            onClick={handleCta}
            data-testid="sales-problem-cta"
            className="rounded-full whitespace-normal h-auto min-h-12 py-3 px-7 text-base font-bold text-[#0B1120] hover:brightness-105 transition-[filter]"
            style={{ background: ACCENT }}
          >
            Get My Sales System Plan <ArrowRight className="ml-1.5 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
}
